import { faCheck, faClock } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { MouseEvent, ReactElement } from 'react'
import {
  wrapper,
  wrapWithHover
} from '~/components/Global/Blocks/Card/Card.css'
import { Pill } from '~/components/Global/Elements/Pill/Pill'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import type { Action } from '~/domains/actions/types'
import { UsersList } from '~/domains/users/components/UsersList'
import type { AsideActionPosition } from '~/types/global'
import { formatDateTimeShort } from '~/utils/date'
import { formatName } from '~/utils/names'
import { useOpenTaskAside } from '../../hooks/useOpenTaskAside'
import { useUpdateTaskMutation } from '../../mutations/updateTask'
import { getTaskDueDate, isTaskOverdue } from '../../utils/groupTasksByDue'
import {
  getTaskColor,
  getTaskIcon,
  getTaskLabel,
  getTaskOutcome,
  getTaskTitle,
  getTaskWordingFor,
  isFreeTask
} from '../../utils/taskType'
import { TaskTypeIcon } from '../TaskTypeIcon/TaskTypeIcon'
import {
  taskCellDue,
  taskCellDueOverdue,
  taskCellDueUnscheduled,
  taskCellMain,
  taskCellMainText,
  taskCellQuickDone,
  taskCellUsers,
  taskCellUsersEmpty,
  taskQuickDoneButton,
  taskRow,
  taskRowCompact,
  taskSubtitle,
  taskTitle
} from './TaskCard.css'
import { TaskPersons } from './TaskPersons'

type TaskCardProps = {
  task: Action
  compact?: boolean
  /** Skip the linked-persons column (parent grid: `taskGridWithoutPersons`). */
  hidePersons?: boolean
  /** Put the type/subject cell first and the due date second (parent grid: `taskGridWithoutPersons`). */
  dueDateAfterTitle?: boolean
  /** Treated tasks list: show the event date instead of the due date. */
  showCompletion?: boolean
  asidePosition?: AsideActionPosition
  asideAvailablePositions?: AsideActionPosition[]
  legacyAside?: boolean
  onOpen?: () => void
}

/**
 * One task as a row of the parent `taskGrid` / `taskGridCompact`:
 * due date, type (or subject for a free task), linked persons, assignee and creator.
 */
export const TaskCard = ({
  task,
  compact = false,
  hidePersons = false,
  dueDateAfterTitle = false,
  showCompletion = false,
  asidePosition,
  asideAvailablePositions,
  legacyAside,
  onOpen
}: TaskCardProps): ReactElement => {
  const openTaskAside = useOpenTaskAside()
  const { mutate: updateTask, isPending } = useUpdateTaskMutation()
  const applicants = Object.values(task.applicants ?? {})

  const dueDate = getTaskDueDate(task)
  const overdue = isTaskOverdue(task)
  const isClosed = task.isCompleted !== null && task.isCompleted !== undefined
  const subject = task.subject?.trim() ? task.subject : null
  const freeTask = isFreeTask(task)
  const title = freeTask ? getTaskTitle(task) : getTaskLabel(task)
  const subtitle = freeTask ? null : subject
  const involvedUsers = [
    ...(task.user ? [task.user] : []),
    ...(task.createdBy && task.createdBy.id !== task.user?.id
      ? [task.createdBy]
      : [])
  ]
  const wording = getTaskWordingFor(task)
  const usersTooltip = [
    task.user
      ? `${wording.assigned} à ${formatName(task.user)}`
      : wording.unassigned,
    ...(task.createdBy && task.createdBy.id !== task.user?.id
      ? [`${wording.created} par ${formatName(task.createdBy)}`]
      : [])
  ].join(' · ')

  const openAside = () => {
    onOpen?.()
    openTaskAside(task, {
      position: asidePosition,
      availablePositions: asideAvailablePositions,
      legacy: legacyAside
    })
  }

  const markDone = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    updateTask({
      taskId: task.id,
      isCompleted: true,
      // Explicit, otherwise the server dates the completion at the scheduled date
      completionDate: new Date(),
      applicantIds: applicants.map((applicant) => applicant.id),
      quickDone: { title: getTaskTitle(task) }
    })
  }

  const treatmentDate = task.eventDate ? new Date(task.eventDate) : null
  const outcome = getTaskOutcome(task)
  const dueCell = showCompletion ? (
    <div className={taskCellDue}>
      <FontAwesomeIcon icon={faCheck} />
      {treatmentDate ? formatDateTimeShort(treatmentDate) : 'Sans date'}
      {outcome && <Pill variant={outcome.variant}>{outcome.label}</Pill>}
    </div>
  ) : (
    <div
      className={clsx(
        taskCellDue,
        overdue && !isClosed && taskCellDueOverdue,
        dueDate === null && taskCellDueUnscheduled
      )}
    >
      <FontAwesomeIcon icon={faClock} />
      {dueDate ? formatDateTimeShort(dueDate) : 'Sans échéance'}
    </div>
  )

  return (
    <div
      className={clsx(
        wrapper,
        wrapWithHover,
        taskRow,
        compact && taskRowCompact
      )}
      onClick={openAside}
      data-onboarding="task-card"
    >
      {!dueDateAfterTitle && dueCell}
      <div className={taskCellMain}>
        <TaskTypeIcon
          icon={getTaskIcon(task)}
          color={getTaskColor(task)}
          small={compact}
        />
        <div className={taskCellMainText}>
          <span className={taskTitle} title={title}>
            {title}
          </span>
          {subtitle && (
            <span className={taskSubtitle} title={subtitle}>
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {dueDateAfterTitle && dueCell}
      {!hidePersons && <TaskPersons task={task} legacyBridges={legacyAside} />}
      <div className={taskCellUsers}>
        {involvedUsers.length > 0 ? (
          <UsersList
            users={involvedUsers}
            tooltipText={usersTooltip}
            align="right"
          />
        ) : (
          <span className={taskCellUsersEmpty}>{wording.unassigned}</span>
        )}
      </div>
      {!showCompletion && (
        <div className={taskCellQuickDone} data-onboarding="task-quick-done">
          {!isClosed && (
            <Tooltip text="Marquer comme faite" direction="top" asChild>
              <Button
                variation={ButtonVariation.Neutral}
                buttonSize="extraSmall"
                narrow
                className={taskQuickDoneButton}
                disabled={isPending}
                onClick={markDone}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}
